import { IconClock } from '@tabler/icons-react'
import capitalize from 'lodash/capitalize'
import { ReactNode } from 'react'

import { AstraBadge, AstraHoverCard, AstraIcon, AstraParagraph, DATE_TEMPLATES, day, format, humanizeDate } from '~/astra'
import { ButtonTag } from '~/components'
import { Breadcrumbs, CrumbItem } from '~/components/BreadcrumbsHeader'

export type CellErrorItem = {
    sources?: string[]
    label: string
    description?: string
}

const ErrorItem = ({ error, showErrorSources }: { error: CellErrorItem; showErrorSources: boolean }) => (
    <>
        <div className="flex flex-wrap gap-0.5">
            <AstraBadge color="critical" text={error.label} variant="filled" size="small" />
            {showErrorSources && error.sources && error.sources.map(source => <AstraBadge key={source} text={source} variant="outlined" size="small" />)}
        </div>
        <AstraParagraph className="min-w-0 pl-1 text-body-small">{error.description}</AstraParagraph>
    </>
)

type EditInfoData = { updated_by?: string | null; updated_at?: string | null }
export const getEditInfoText = ({ updated_by, updated_at }: EditInfoData) =>
    `Sist endret ${updated_by ? `av ${updated_by} ` : ''}${humanizeDate(day(updated_at))}`

export const getDateBreadcrumb = (date: string) => ({ label: capitalize(format(day(date), DATE_TEMPLATES.fullWeekDayMonth)) })

export const EditInfoFooter = ({ editInfo }: { editInfo: string }) => (
    <>
        <span className="mr-1 inline-block align-text-bottom">
            <AstraIcon icon={IconClock} size="small" />
        </span>
        {editInfo}
    </>
)

type TriggerInfo = {
    shortName: string
    label?: string
}

type CellErrorHoverCardProps = {
    children: ReactNode
    footer?: ReactNode
    triggerInfo: TriggerInfo
    errors: CellErrorItem[]
    breadcrumbs: CrumbItem[]
    showErrorSources?: boolean
}

export const CellErrorHoverCard = ({ children, breadcrumbs, footer, triggerInfo, errors, showErrorSources = false }: CellErrorHoverCardProps) => (
    <AstraHoverCard
        noPadding
        placement="top"
        showArrow
        hoverContent={
            <div className="flex max-w-xs flex-col">
                <div className="flex items-center justify-between gap-4 border-b border-base px-1 py-2 pr-3 text-default">
                    <Breadcrumbs variant="compact" crumbs={breadcrumbs} />
                    <ButtonTag
                        className="w-min min-w-6 shrink-0 px-1"
                        colorType="room_conflict"
                        label={triggerInfo.shortName}
                        fillType="all"
                        smartTruncate={false}
                    />
                </div>
                <div className="flex flex-col gap-1.5 px-3 py-2 text-body-small">
                    {triggerInfo.label && (
                        <AstraParagraph className="text-body-small" weight="medium">
                            {triggerInfo.label}
                        </AstraParagraph>
                    )}
                    <div className="grid max-h-40 w-full grid-cols-2 items-start gap-x-1 gap-y-2 overflow-y-auto">
                        {errors.map((error, index) => (
                            <ErrorItem key={index} error={error} showErrorSources={showErrorSources} />
                        ))}
                    </div>
                </div>
                {footer && <div className="border-t border-base px-3 py-1 text-body-extra-small text-muted">{footer}</div>}
            </div>
        }
    >
        {children}
    </AstraHoverCard>
)
